import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';

/**
 * GPT-5-nano settings component for adjusting reasoning effort and verbosity parameters.
 * Integrates with useChatManager hook to manage GPT-5 specific settings.
 * 
 * @param {object} props - Component props.
 * @param {string} props.className - Additional CSS classes to apply to the component.
 * @param {string} props.reasoningEffort - Current reasoning effort setting.
 * @param {string} props.verbosity - Current verbosity setting.
 * @param {function} props.setReasoningEffort - Function to update reasoning effort.
 * @param {function} props.setVerbosity - Function to update verbosity level.
 * @param {function} props.resetGPT5Parameters - Function to reset parameters to defaults.
 * @param {boolean} props.isGPT5Enabled - Whether GPT-5 features are enabled.
 */
export default function GPT5Settings({
    className = "",
    reasoningEffort,
    verbosity,
    setReasoningEffort,
    setVerbosity,
    resetGPT5Parameters,
    isGPT5Enabled
}) {
    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const [changeNotification, setChangeNotification] = useState('');

    // Show notification when parameters change
    useEffect(() => {
        if (changeNotification) {
            const timer = setTimeout(() => setChangeNotification(''), 2000);
            return () => clearTimeout(timer);
        }
    }, [changeNotification]);

    // Parameter descriptions for user guidance
    const reasoningEffortDescriptions = {
        low: "Faster responses with minimal reasoning",
        medium: "Balanced speed and reasoning quality", 
        high: "Thorough reasoning for complex tasks"
    };

    const verbosityDescriptions = {
        low: "Concise, brief responses",
        medium: "Balanced detail level",
        high: "Detailed, comprehensive responses"
    };

    // Handle parameter changes with validation
    const handleReasoningEffortChange = (value) => {
        if (isGPT5Enabled && setReasoningEffort) {
            setReasoningEffort(value);
            setChangeNotification(`Reasoning effort updated to ${value}`);
        }
    };

    const handleVerbosityChange = (value) => {
        if (isGPT5Enabled && setVerbosity) {
            setVerbosity(value);
            setChangeNotification(`Verbosity updated to ${value}`);
        }
    };

    // Handle reset with confirmation
    const handleReset = () => {
        if (showResetConfirmation) {
            if (resetGPT5Parameters) {
                resetGPT5Parameters();
                setChangeNotification('Settings reset to defaults');
            }
            setShowResetConfirmation(false);
        } else {
            setShowResetConfirmation(true);
            // Auto-hide confirmation after 3 seconds
            setTimeout(() => setShowResetConfirmation(false), 3000);
        }
    };

    // Don't render if GPT-5 is not enabled
    if (!isGPT5Enabled) {
        return null;
    }

    return (
        <Card className={`w-full max-w-md ${className}`}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    GPT-5-nano Settings
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Enabled
                    </span>
                </CardTitle>
                <CardDescription>
                    Adjust reasoning effort and response verbosity for GPT-5-nano interactions
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                {/* Change Notification */}
                {changeNotification && (
                    <Alert className="border-green-200 bg-green-50 text-green-800">
                        <AlertDescription className="text-sm">
                            {changeNotification}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Reasoning Effort Setting */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Reasoning Effort</label>
                        <span className="text-xs text-muted-foreground capitalize">
                            {reasoningEffort}
                        </span>
                    </div>
                    
                    <Select 
                        value={reasoningEffort} 
                        onValueChange={handleReasoningEffortChange}
                        disabled={!isGPT5Enabled}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select reasoning effort" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="low">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">Low</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="medium">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">Medium</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="high">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">High</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    
                    <p className="text-xs text-muted-foreground">
                        {reasoningEffortDescriptions[reasoningEffort]}
                    </p>
                </div>

                {/* Verbosity Setting */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Verbosity</label>
                        <span className="text-xs text-muted-foreground capitalize">
                            {verbosity}
                        </span>
                    </div>
                    
                    <Select 
                        value={verbosity} 
                        onValueChange={handleVerbosityChange}
                        disabled={!isGPT5Enabled}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select verbosity level" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="low">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">Low</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="medium">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">Medium</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="high">
                                <div className="flex flex-col items-start">
                                    <span className="font-medium">High</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    
                    <p className="text-xs text-muted-foreground">
                        {verbosityDescriptions[verbosity]}
                    </p>
                </div>

                {/* Current Settings Summary */}
                <div className="rounded-md bg-muted p-3 space-y-1">
                    <div className="text-sm font-medium">Current Settings:</div>
                    <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                            <span>Reasoning:</span>
                            <span className="capitalize font-medium">{reasoningEffort}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Verbosity:</span>
                            <span className="capitalize font-medium">{verbosity}</span>
                        </div>
                    </div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    disabled={!isGPT5Enabled}
                    className={showResetConfirmation ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                >
                    {showResetConfirmation ? "Confirm Reset" : "Reset to Defaults"}
                </Button>
                
                <div className="text-xs text-muted-foreground">
                    {showResetConfirmation && "Click again to confirm"}
                </div>
            </CardFooter>
        </Card>
    );
}